import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetBillingDetailsResponse,
  BillingInvoice,
  CardInfo,
  UpdatePaymentMethodResponse,
} from '@dreamai/types';
import api from '../../api';
import './billing-details-section.scss';

const formatCurrency = (amount: number | null, currency: string): string => {
  if (amount === null) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const BRAND_ICONS: Record<string, string> = {
  visa: '/card-icons/visa.svg',
  mastercard: '/card-icons/mastercard.svg',
  'american express': '/card-icons/amex.svg',
  amex: '/card-icons/amex.svg',
  maestro: '/card-icons/maestro.svg',
  jcb: '/card-icons/jcb.svg',
  discover: '/card-icons/discover.svg',
};

const GENERIC_CARD_ICON = '/card-icons/generic.svg';

const brandLabel = (brand: string): string => {
  const map: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'Amex',
    'american express': 'Amex',
    discover: 'Discover',
    jcb: 'JCB',
    'diners club international': 'Diners Club',
    'union pay': 'UnionPay',
  };
  return map[brand.toLowerCase()] || brand;
};

const BillingDetailsSection: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState<CardInfo | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [nextBillingAt, setNextBillingAt] = useState<string | null>(null);
  const [updatingCard, setUpdatingCard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<GetBillingDetailsResponse>(
          '/accounts/billing-details',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCard(data.card);
        setInvoices(data.invoices);
        setNextBillingAt(data.nextBillingAt);
      } catch (err) {
        console.error('Error fetching billing details:', err);
        setError('Failed to load billing details');
      } finally {
        setLoading(false);
      }
    };
    fetchBilling();
  }, [getAccessTokenSilently]);

  const handleUpdateCard = useCallback(async () => {
    setUpdatingCard(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.post<UpdatePaymentMethodResponse>(
        '/accounts/update-payment-method',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = data.checkoutUrl;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(
        axiosErr?.response?.data?.error ||
          'Failed to start card update. Please try again.',
      );
      setUpdatingCard(false);
    }
  }, [getAccessTokenSilently]);

  if (loading) {
    return (
      <div className='billing-details-section'>
        <div className='settings-skeleton' />
      </div>
    );
  }

  return (
    <div className='billing-details-section'>
      <div className='billing-header'>
        <h2 className='section-title'>Billing details</h2>
        <p className='billing-subtitle'>
          Manage your payment method and view invoices.
        </p>
      </div>

      {error && <p className='billing-error'>{error}</p>}

      {/* Payment method card */}
      <div className='billing-card'>
        <div className='billing-card-header'>
          <h3 className='billing-card-title'>Payment method</h3>
          <button
            className='btn-update-card'
            onClick={handleUpdateCard}
            disabled={updatingCard}
          >
            {updatingCard
              ? 'Redirecting...'
              : card
                ? 'Update card'
                : 'Add card'}
          </button>
        </div>

        {card ? (
          <div className='card-display'>
            <div className='card-icon'>
              <img
                src={BRAND_ICONS[card.brand.toLowerCase()] || GENERIC_CARD_ICON}
                alt={card.brand}
                width='32'
                height='32'
              />
            </div>
            <div className='card-info'>
              <p className='card-brand-last4'>
                {brandLabel(card.brand)} ending in {card.last4}
              </p>
              {nextBillingAt && (
                <p className='card-next-billing'>
                  Next billing: {formatDate(nextBillingAt)}
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className='no-card'>No payment method on file.</p>
        )}
      </div>

      {/* Invoices */}
      <div className='billing-card'>
        <h3 className='billing-card-title'>Invoices</h3>

        {invoices.length === 0 ? (
          <p className='no-invoices'>No invoices yet.</p>
        ) : (
          <div className='invoice-table-wrapper'>
            <table className='invoice-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td className='invoice-date'>
                      {formatDate(inv.createdAt)}
                    </td>
                    <td className='invoice-number'>
                      {inv.number || inv.id.slice(0, 12)}
                    </td>
                    <td className='invoice-amount'>
                      {formatCurrency(inv.totalAmount, inv.currency)}
                    </td>
                    <td>
                      <span
                        className={`invoice-status ${
                          inv.paymentStatus === 'PAID' ? 'paid' : 'unpaid'
                        }`}
                      >
                        {inv.paymentStatus === 'PAID' ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                    <td className='invoice-actions'>
                      {inv.pdfUrl && (
                        <a
                          href={inv.pdfUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='invoice-link'
                          title='Download PDF'
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          >
                            <path d='M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4' />
                            <polyline points='7 10 12 15 17 10' />
                            <line x1='12' y1='15' x2='12' y2='3' />
                          </svg>
                        </a>
                      )}
                      {inv.hostedUrl && (
                        <a
                          href={inv.hostedUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='invoice-link'
                          title='View invoice'
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          >
                            <path d='M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6' />
                            <polyline points='15 3 21 3 21 9' />
                            <line x1='10' y1='14' x2='21' y2='3' />
                          </svg>
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingDetailsSection;
